import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { AbstractControl, ControlContainer, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { NextcloudFile, NotificationTask, NotificationTaskType } from 'src/app/classes';
import { quillOptions } from '../../../globals';
import { DefaultMail, Issue, IssueTransaction } from 'src/app/lib/issue/classes';
import { IIssueProviderService, IIssueTransactionProviderService, ISSUE_PROVIDER_SERVICE, ISSUE_TRANSACTION_PROVIDER_SERVICE } from '../../../interfaces';
import { ContactService } from 'src/app/lib/customer/services/contact.service';
import { CustomerService } from 'src/app/lib/customer/services/customer.service';
import { Contact, Customer } from 'src/app/lib/customer/classes';
import { filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { FileService } from 'src/app/services/file.service';
import { ISSUE_FORM_GROUP } from '../../dialog/issue-transaction-dialog2/issue-transaction-dialog2-component-classes';
import { NotificationPlaceholderReplacementService } from '../../../services/notification-placeholder-replacement.service';
import { UserService } from 'src/app/lib/user/services/user.service';
import { User } from 'src/app/lib/user/classes';
import { DefaultNotification } from 'src/app/lib/default-notification/classes';
import { MatSnackBar } from '@angular/material/snack-bar';
import { regularExpressions } from 'src/app/globals';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

@Component({
  selector: 'app-notification-content',
  templateUrl: './notification-content.component.html',
  styleUrls: ['./notification-content.component.css']
})
export class NotificationContentComponent implements OnDestroy, OnInit {

  @Input() issueTemplate: Issue;

  separatorKeysCodes: number[] = [ENTER, COMMA];

  drafts: DefaultNotification[] = [];

  receiverSuggestions: ReceiverSuggestion[] = [];

  formGroup: FormGroup;
  public receiverFormControl: FormControl = new FormControl(null, Validators.compose([Validators.required, Validators.email]));

  attachedFilesReadonly: { [key: string]: boolean } = {};

  nextcloudFolder: NextcloudFile[] = [];

  fixedReceiverSet: boolean = false;

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(ISSUE_TRANSACTION_PROVIDER_SERVICE) private _issueProviderTransactionService: IIssueTransactionProviderService,
    @Inject(ISSUE_PROVIDER_SERVICE) private _issueProviderService: IIssueProviderService,
    @Optional() @Inject(ISSUE_FORM_GROUP) public issueFormGroup: FormGroup,
    @Optional() private _controlContainer: ControlContainer,
    private _userService: UserService,
    private _fileService: FileService,
    private _contactService: ContactService,
    private _customerService: CustomerService,
    private _notificationPlaceholderReplacementService: NotificationPlaceholderReplacementService,
    private _snackBar: MatSnackBar
  ) { }

  addDistinctReceiver(wrapper: ReceiverSuggestion) {
    let index = this.receiverSuggestions.findIndex(x => x.value === wrapper.value);
    if (index === -1) this.receiverSuggestions.push(wrapper);
  }

  addReceiver(receiver: string) {
    let index = (this.receiverControl.value as string[]).findIndex(x => x === receiver);
    if (index > -1) {
      this._snackBar.open('Empfänger können nicht doppelt gewählt werden.', 'Ok', { duration: 2000 });
      return;
    }
    if ((this.receiverFormControl.value as string).search(regularExpressions.EMailValidation) === -1) {
      this._snackBar.open('Invalide E-Mail Adresse erkannt.', 'Ok', { duration: 2000 });
      return;
    }
    (this.receiverControl.value as string[]).push(receiver);
    this.receiverFormControl.setValue(null);
  }

  fileClicked(event: MouseEvent, file: NextcloudFile) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!Array.isArray(this.attachements.controls)) this.attachements.setValue([]);
    if (this.attachedFilesReadonly[file.uuid]) {
      let index = this.attachements.controls.findIndex(x => x.value.uuid === file.uuid);
      if (index > -1) {
        this.attachements.removeAt(index);
        this.attachedFilesReadonly[file.uuid] = false;
      }
    } else {
      let index = this.attachements.controls.findIndex(x => x.value.uuid === file.uuid);
      if (index < 0) {
        this.attachements.push(new FormGroup({
          'href': new FormControl(file.href),
          'uuid': new FormControl(file.uuid),
          'type': new FormControl(file.type),
          'isNextcloudFile': new FormControl(true)
        }));
        this.attachedFilesReadonly[file.uuid] = true;
      }
    }
  }

  initType(
    type: any,
    issue: Issue,
    issueTransaction: IssueTransaction = null,
    defaultMails: DefaultMail[] = [],
    defaultNotifications: DefaultNotification[] = []
  ) {

    this.receiverSuggestions = [];

    switch (type) {

      case NotificationTaskType.EMail:
        this.fixedReceiverSet = false;
        this.setEMailReceivers();
        break;

      case NotificationTaskType.Internal:
        this.fixedReceiverSet = true;
        this._userService.users.subscribe((users: User[]) => {
          this.receiverSuggestions = users.map(x => {
            return {
              'display': `${x.userName}`,
              'value': x.id
            } as ReceiverSuggestion;
          })
        });
        break;
    }

    this.drafts = defaultNotifications;
  }

  ngOnDestroy() {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
    if (typeof this.receiverFormControl.value === 'string' && (this.receiverFormControl.value as string).search(regularExpressions.EMailValidation) > -1) {
      let index = (this.receiverControl.value as string[]).findIndex(x => x === this.receiverFormControl.value);
      if (index === -1) (this.receiverControl.value as string[]).push(this.receiverFormControl.value);
    }
  }

  ngOnInit(): void {

    this.formGroup = <FormGroup>this._controlContainer.control;

    this._subscriptions.push(...[
      this._issueProviderService.issue$.pipe(filter(x => x && x.folderlocation ? true : false), map(x => x.folderlocation), switchMap((x: string) => this._fileService.getFolderByPath(x))).subscribe((files: NextcloudFile[]) => this.nextcloudFolder = files),
      combineLatest([this._issueProviderService.issue$, this._issueProviderTransactionService.issueTransaction$.pipe(startWith(null)), this._issueProviderTransactionService.issueTransactionDefaultMails$.pipe(startWith([])), this._issueProviderTransactionService.issueTransactionDefaultNotifications$.pipe(startWith([]))]).subscribe(([issue, issueTransaction, defaultMails, defaultNotifications]) => this.initType(this.typeControl.value, issue, issueTransaction, defaultMails, defaultNotifications))
    ]);

    for (let attachement of this.attachements.controls) this.attachedFilesReadonly[(attachement.value as NextcloudFile).uuid] = true;
  }

  removeReceiver(receiver: string) {
    let index = (this.receiverControl.value as string[]).findIndex(x => x === receiver);
    if (index > -1) this.receiverControl.value.splice(index, 1);
  }

  setDraft(draft: DefaultNotification) {
    combineLatest([this._issueProviderTransactionService.issueTransaction$]).pipe(take(1), switchMap(([issueTransaction]) => {
      return this._notificationPlaceholderReplacementService
        .replace({
          'content': draft.htmlContent,
          'subject': draft.subject
        } as NotificationTask, this.issueFormGroup.value, issueTransaction);
    })).subscribe((result: NotificationTask) => {
      this.subjectControl.setValue(result.subject);
      this.contentControl.setValue(result.content);
    })
  }

  setEMailReceivers() {
    if (this.issueFormGroup.value.commercialcontactpersonalid) this._contactService.getContactById(this.issueFormGroup.value.commercialcontactpersonalid, true).subscribe((x: Contact) => {
      let eMails = [];
      if (x.contactpersmail) eMails.push(x.contactpersmail);
      for (let mail of x.contactinformation.filter(x => x.infotype === 1)) eMails.push(mail.infovalue);
      eMails.forEach(y => this.addDistinctReceiver({
        'display': `E-Mail an geschäftlichen Ansprechpartner (${y})`,
        'value': y
      } as ReceiverSuggestion));
    });
    if (this.issueFormGroup.value.localcontactpersonalid) this._contactService.getContactById(this.issueFormGroup.value.localcontactpersonalid, true).subscribe((x: Contact) => {
      let eMails = [];
      if (x.contactpersmail) eMails.push(x.contactpersmail);
      for (let mail of x.contactinformation.filter(x => x.infotype === 1)) eMails.push(mail.infovalue);
      eMails.forEach(y => this.addDistinctReceiver({
        'display': `E-Mail an geschäftlichen Ansprechpartner (${y})`,
        'value': y
      } as ReceiverSuggestion));
    });
    if (this.issueFormGroup.value.issuecustomer) this._customerService.getCustomerById(this.issueFormGroup.value.issuecustomer).subscribe((x: Customer) => {
      if (x.customermail) this.addDistinctReceiver({
        'display': `E-Mail an Kunde (${x.customermail})`,
        'value': x.customermail
      } as ReceiverSuggestion);
    });
  }

  tryAddEMail() {
    let candidate = this.receiverFormControl.value;
    if (this.receiverFormControl.valid) {
      if((this.receiverControl.value as string[]).indexOf(candidate) > -1) return;
      this.receiverControl.setValue([...this.receiverControl.value as string[], candidate]);
    }
    this.receiverFormControl.setValue(null);
  }

  get NotificationTaskType() {
    return NotificationTaskType;
  }

  // controls
  get attachements(): FormArray {
    return this.formGroup.controls['attachements'] as FormArray;
  }
  get isDefaultNotificationControl(): AbstractControl {
    return this.formGroup.controls['isDefaultNotification'];
  }
  get contentControl(): AbstractControl {
    return this.formGroup.controls['content'];
  }
  get receiverControl(): AbstractControl {
    return this.formGroup.controls['receiver'];
  }
  get subjectControl(): AbstractControl {
    return this.formGroup.controls['subject'];
  }
  get typeControl(): AbstractControl {
    return this.formGroup.controls['type'];
  }

  // quill
  public quillModules = quillOptions;

}

export class ReceiverSuggestion {
  value: string;
  display: string;
}
